import { PaymentInfo } from '@/api/payments'
import {
  Column,
  FormAddressAutocomplete,
  FormCheckBox,
  Loader,
  Typography,
} from '@/components'
import { ThemeColor } from '@/enums'
import { PaymentMethodList } from '@/modules/checkout/components/PaymentMethodList'
import { useFormContext, useSnackbar } from '@/providers'
import {
  useCompany,
  useCompanyPayments,
  useCreatePayment,
  useUpdateAddress,
  useUpdateCompany,
} from '@/services'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useValidateCheckoutStep } from './helpers'
import { useOutletContext, useParams } from 'react-router-dom'

interface CartItem {
  categoryId: string
}

interface Cart {
  [locationId: string]: CartItem[]
}

interface CompanyDetailsAndPaymentStepFormValues {
  companyDetails: {
    name: string
    address: {
      addressId: number
      street: string
      streetNr: string
      postalCode: string
      city: string
      country: string
    }
  }
  payment: {
    paymentId: string
  }
  sepa: boolean
  newPaymentName: string
  newPaymentIban: string
  newPaymentIsDefault: boolean
}

const CompanyDetailsForm = () => {
  const { t } = useTranslation()

  return (
    <Column gap="8px">
      <Column gap="4px">
        <Typography bold variant="h4">
          {t('checkout.titles.companyDetails')}
        </Typography>
        <Typography color={ThemeColor.glass500} variant="description">
          {t('checkout.subtitles.companyDetails')}
        </Typography>
      </Column>
      <FormAddressAutocomplete name="companyDetails" />
    </Column>
  )
}

const PaymentForm = ({
  payments,
  companyId,
}: {
  payments: PaymentInfo[]
  companyId: string
}) => {
  const { t } = useTranslation()
  const {
    values,
    customValidate,
    setFieldValue,
  } = useFormContext<CompanyDetailsAndPaymentStepFormValues>()
  const createPaymentMutation = useCreatePayment()

  const handleCreatePayment = (callback: () => void) => {
    customValidate({
      fields: ['newPaymentName', 'newPaymentIban', 'newPaymentIsDefault'],
      onSuccess: async () => {
        const { paymentId } = await createPaymentMutation.mutateAsync({
          name: values.newPaymentName,
          iban: values.newPaymentIban,
          bic: '',
          priority: values.newPaymentIsDefault ? 1 : 0,
          companyId: companyId ?? '',
        })
        setFieldValue('newPaymentName', '')
        setFieldValue('newPaymentIban', '')
        setFieldValue('newPaymentIsDefault', false)

        if (values.newPaymentIsDefault) {
          setFieldValue('payment.paymentId', paymentId)
        }
        callback()
      },
    })
  }

  return (
    <Column gap="8px">
      <Column gap="4px">
        <Typography bold variant="h4">
          {t('checkout.titles.payment')}
        </Typography>
      </Column>
      <Column gap="24px">
        <PaymentMethodList
          payments={payments}
          onCreatePaymentSubmit={handleCreatePayment}
          isLoading={createPaymentMutation.isLoading}
        />
        <FormCheckBox id="sepa" label={t('sepa')} name="sepa" noMargin />
      </Column>
    </Column>
  )
}

interface SetButtonStateParams {
  onClick?: () => void
  loading: boolean
}

interface CompanyDetailsStepProps {
  setButtonState: (buttonState: SetButtonStateParams) => void
  companyId: string
  onNext: () => void
  poaSigned: boolean
  isGenericCompany: boolean
  isLast: boolean
  submitCheckout: (params: { paymentId?: string; items: CartItem[] }) => void
  submitCheckoutLoading: boolean
}

export const CompanyDetailsAndPaymentStep = ({
  setButtonState,
  companyId,
  onNext,
  poaSigned,
  isLast,
  submitCheckout,
  submitCheckoutLoading,
}: CompanyDetailsStepProps) => {
  const { t } = useTranslation()
  const { locationId = '' } = useParams()
  const { cart } = useOutletContext<{ cart: Cart }>()
  const snackbar = useSnackbar()
  const {
    values,
    setFieldValue,
  } = useFormContext<CompanyDetailsAndPaymentStepFormValues>()

  const { data: companyData, isLoading: isCompanyLoading } = useCompany(
    companyId,
  )
  const updateCompanyMutation = useUpdateCompany(companyId)
  const updateAddressMutation = useUpdateAddress(
    companyData?.address?.addressId ?? 0,
  )
  const { data: payments, isLoading: isPaymentsLoading } = useCompanyPayments(
    companyId,
  )

  const shouldShowCompanyDetails = !poaSigned

  const cartItems = locationId ? cart[locationId] : []

  const handleSubmit = async () => {
    const addressWithCountry = {
      ...values.companyDetails.address,
      country: 'Germany',
    }
    try {
      if (shouldShowCompanyDetails) {
        await Promise.all([
          updateCompanyMutation.mutateAsync({
            name: values.companyDetails.name,
          }),
          updateAddressMutation.mutateAsync(addressWithCountry),
        ])
      }

      if (isLast) {
        submitCheckout({
          paymentId: values?.payment?.paymentId,
          items: cartItems,
        })
      } else {
        onNext()
      }
    } catch (_e) {
      snackbar.error(t('snackbars.error.generalMsg'))
    }
  }

  const handleNext = useValidateCheckoutStep(handleSubmit, [
    'sepa',
    'payment',
    'companyDetails',
  ])

  useEffect(() => {
    if (!isCompanyLoading && !isPaymentsLoading) {
      const priorityPayment = payments?.find(
        (payment) => payment.priority === 1,
      )
      if (!values?.payment?.paymentId) {
        if (priorityPayment) {
          setFieldValue('payment.paymentId', priorityPayment.paymentId)
        } else if (payments && payments.length > 0) {
          setFieldValue('payment.paymentId', payments[0].paymentId)
        }
      }
    }
  }, [isCompanyLoading, isPaymentsLoading, payments, values, setFieldValue])

  useEffect(() => {
    setButtonState({
      onClick: handleNext,
      loading:
        updateAddressMutation.isLoading ||
        updateCompanyMutation.isLoading ||
        submitCheckoutLoading,
    })

    return () => {
      setButtonState({ loading: false })
    }
  }, [
    values,
    updateAddressMutation.isLoading,
    updateCompanyMutation.isLoading,
    submitCheckoutLoading,
  ])

  if (isCompanyLoading || isPaymentsLoading) {
    return <Loader />
  }

  return (
    <Column gap="32px">
      {shouldShowCompanyDetails ? <CompanyDetailsForm /> : null}
      <PaymentForm companyId={companyId} payments={payments ?? []} />
    </Column>
  )
}
